import { Button } from "@/components/ui/button";
import {
  MetaAPI,
  useConnectMetaPage,
  useMetaSettings,
  type MetaPage,
} from "@/features/connectors";
import {
  MetaLoginCancelled,
  openMetaLogin,
} from "@/features/connectors/meta-popup";
import { useTranslation } from "@/features/utils/i18n";
import { CircleNotch } from "@phosphor-icons/react";
import { useState } from "react";
import { toast } from "sonner";

interface Props {
  clientId: string;
  channel: "messenger" | "instagram";
  agentId: string;
  onConnected: () => void;
}

/**
 * Messenger and Instagram: sign in with Facebook, pick the Page (or the
 * Instagram account linked to it). The page tokens stay on the server.
 */
export const PagesPanel = ({ clientId, channel, agentId, onConnected }: Props) => {
  const { t } = useTranslation("connectors");
  const { data: settings } = useMetaSettings(clientId);
  const connect = useConnectMetaPage();
  const [busy, setBusy] = useState(false);
  const [handle, setHandle] = useState("");
  const [pages, setPages] = useState<MetaPage[] | null>(null);
  const [selected, setSelected] = useState("");

  const start = async () => {
    if (!settings) return;
    setBusy(true);
    try {
      // The popup opens on the click; the dialog's URL follows.
      const login = await openMetaLogin(MetaAPI.authorizeUrl(clientId, channel));
      const result = await MetaAPI.getPages(clientId, login.handle);
      setHandle(login.handle);
      setPages(result.pages);
      setSelected("");
    } catch (error) {
      if (error instanceof MetaLoginCancelled) return;
      toast.error(
        error instanceof Error ? error.message : t("meta.connectError"),
      );
    } finally {
      setBusy(false);
    }
  };

  const finish = async () => {
    try {
      await connect.mutateAsync({
        clientId,
        handle,
        page_id: selected,
        channel,
        agent_id: agentId || undefined,
      });
      toast.success(t("meta.connected"));
      onConnected();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t("meta.connectError"),
      );
    }
  };

  // An Instagram account is reached through its Page: a Page without one
  // cannot be chosen for it.
  const choices = (pages ?? []).filter(
    (page) => channel === "messenger" || page.instagram,
  );

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        {t(`meta.${channel}.intro`)}
      </p>

      {pages === null ? (
        <Button type="button" onClick={start} disabled={busy || !settings}>
          {busy && <CircleNotch className="h-4 w-4 animate-spin" />}
          {t("meta.continueWithFacebook")}
        </Button>
      ) : choices.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {t(`meta.${channel}.noPage`)}
        </p>
      ) : (
        <div className="space-y-3">
          <div className="space-y-2" role="radiogroup">
            {choices.map((page) => (
              <label
                key={page.id}
                className="flex cursor-pointer items-center gap-3 rounded-lg border p-3 has-[:checked]:border-primary"
              >
                <input
                  type="radio"
                  name="meta-page"
                  value={page.id}
                  checked={selected === page.id}
                  onChange={() => setSelected(page.id)}
                />
                <span className="text-sm">
                  {channel === "instagram" && page.instagram?.username
                    ? `@${page.instagram.username}`
                    : page.name}
                  {channel === "instagram" && (
                    <span className="text-muted-foreground"> · {page.name}</span>
                  )}
                </span>
              </label>
            ))}
          </div>
          <Button
            type="button"
            onClick={finish}
            disabled={!selected || connect.isPending}
          >
            {connect.isPending && (
              <CircleNotch className="h-4 w-4 animate-spin" />
            )}
            {t("meta.connect")}
          </Button>
        </div>
      )}
    </div>
  );
};
